import { SelectField, TextField } from '~/common/components';
import { useI18n, Trans } from '~/common/context';
import {
  FormContainer,
  Title,
  Col,
  Row,
  Label,
  Switch,
  Button,
  Flex,
} from '@wyz/ui';
import { useForm, useWatch } from 'react-hook-form';
import { ICountry, ICountryConfigurationForm } from '~/connect/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  COUNTRY_STATUS,
  countryConfigurationFormSchema,
} from '~/connect/schemas';
import { useManageInvoicingCompanies } from '~/connect/api';
import { useOnSuccessQuery } from '@wyz/utils';
import { isNil } from 'lodash-es';
import { useManageTenants } from '~/common/api';

const DEFAULT_VALUES: Partial<ICountryConfigurationForm> = {
  status: COUNTRY_STATUS.INACTIVE,
  contact: {
    email: '',
    phone: '',
  },
  labelEmailQuote: '',
  labelEmailRedistribution: '',
  chatId: '',
};

type Props = {
  defaultValues?: Partial<ICountryConfigurationForm>;
  onSubmit: (data: ICountryConfigurationForm) => void;
  country: ICountry;
  isLoading: boolean;
};

export function CountryConfigurationForm({
  defaultValues = DEFAULT_VALUES,
  onSubmit,
  country,
  isLoading,
}: Props) {
  const { t } = useI18n();

  const manageTenant = useManageTenants({
    getCurrent: {
      enabled: true,
    },
  });

  const methods = useForm<ICountryConfigurationForm>({
    defaultValues: {
      ...defaultValues,
      labelEmailQuote: !country.labelEmailQuote
        ? String(t('app.common.default_no_reply'))
        : country.labelEmailQuote,
      labelEmailRedistribution: !country.labelEmailRedistribution
        ? String(t('app.common.default_no_reply'))
        : country.labelEmailRedistribution,
      defaultLabel: !country.defaultLabel
        ? String(t('app.common.default_no_reply'))
        : country.defaultLabel,
      chatId: !country.chatId ? '' : country.chatId,
    },
    resolver: zodResolver(countryConfigurationFormSchema),
  });

  const watchedStatus = useWatch({
    name: 'status',
    control: methods.control,
  });

  const manageCompanies = useManageInvoicingCompanies({
    list: { enabled: true },
  });

  const invoicingCompanyOptions = manageCompanies.list.isSuccess
    ? manageCompanies.list.data.data.map((company) => ({
        value: company.id,
        label: company.name,
      }))
    : [];

  useOnSuccessQuery(manageCompanies.list, () => {
    if (
      invoicingCompanyOptions.length === 1 &&
      isNil(methods.getValues('invoicingCompanyId'))
    ) {
      methods.setValue('invoicingCompanyId', invoicingCompanyOptions[0].value);
    }
  });

  return (
    <FormContainer methods={methods} submitHandler={onSubmit}>
      <Title className='mb-section' heading='lg' weight='bold' wrap>
        <Trans
          i18nKey='app.country.form.configuration.title'
          values={{
            countryName: country.name,
          }}
        />
      </Title>
      <div className='mb-section'>
        <Row>
          <Col>
            <Label
              htmlFor={`${country.externalId}-status-label`}
              className='capitalize mb-2'
            >
              <Trans i18nKey='app.country.form.configuration.status' />
            </Label>
          </Col>
          <Col>
            <Switch
              defaultChecked={country.status === COUNTRY_STATUS.ACTIVE}
              onChange={(event) => {
                methods.setValue(
                  'status',
                  event.target.checked
                    ? COUNTRY_STATUS.ACTIVE
                    : COUNTRY_STATUS.INACTIVE,
                );
              }}
              id={`${country.externalId}-status`}
            />
          </Col>
        </Row>
        {watchedStatus === COUNTRY_STATUS.ACTIVE && (
          <>
            <Row>
              <TextField
                id={`${country.externalId}-contact-email`}
                name='contact.email'
                required
                label={
                  <Trans i18nKey='app.country.form.configuration.contact.email' />
                }
              />
            </Row>
            <Row>
              <TextField
                id={`${country.externalId}-contact-phone`}
                required
                name='contact.phone'
                label={
                  <Trans i18nKey='app.country.form.configuration.contact.phone' />
                }
              />
            </Row>
            <Row>
              <SelectField
                label={
                  <Trans i18nKey='app.country.form.configuration.wyz_company' />
                }
                name='invoicingCompanyId'
                isLoading={manageCompanies.list.isLoading}
                options={invoicingCompanyOptions}
                required
              />
            </Row>
            <Row>
              <TextField
                id={`${country.externalId}-label-email-default`}
                name='defaultLabel'
                label={
                  <Trans i18nKey='app.country.form.configuration.label.email.default' />
                }
                required
              />
            </Row>
            <Row
              style={{
                display: manageTenant.getCurrent.data?.data.features
                  ?.enabledQuote
                  ? 'block'
                  : 'none',
              }}
            >
              <TextField
                id={`${country.externalId}-label-email-quote`}
                name='labelEmailQuote'
                label={t('app.country.form.configuration.label.email.quote')}
                required={
                  manageTenant.getCurrent.data?.data.features?.enabledQuote
                }
              />
            </Row>
            <Row
              style={{
                display: manageTenant.getCurrent.data?.data.features
                  ?.enabledRedistribution
                  ? 'block'
                  : 'none',
              }}
            >
              <TextField
                id={`${country.externalId}-label-email-redistrib`}
                name='labelEmailRedistribution'
                label={
                  <Trans i18nKey='app.country.form.configuration.label.email.redistrib' />
                }
                required={
                  manageTenant.getCurrent.data?.data.features
                    ?.enabledRedistribution
                }
              />
            </Row>
            <Row
              style={{
                display: manageTenant.getCurrent.data?.data.features
                  ?.enabledChat
                  ? 'block'
                  : 'none',
              }}
            >
              <TextField
                id={`${country.externalId}-chat-id`}
                name='chatId'
                label={
                  <Trans i18nKey='app.country.form.configuration.chat.id' />
                }
              />
            </Row>
          </>
        )}
      </div>
      <Flex alignItems='center' justifyContent='end'>
        <Button
          id='submit'
          variant='primary'
          type='submit'
          isLoading={isLoading}
        >
          <Trans i18nKey='app.country.form.configuration.submit_btn' />
        </Button>
      </Flex>
    </FormContainer>
  );
}
